import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { useState } from "react";

import { organizationsCreateOrganization } from "../api-refs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Spinner } from "../components/ui/spinner";

export const Route = createFileRoute("/create-organization")({
  component: CreateOrganizationPage,
});

function CreateOrganizationPage() {
  const navigate = useNavigate();
  const createOrg = useMutation(organizationsCreateOrganization);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      await createOrg({
        name: trimmed,
        ...(slug.trim().length > 0 ? { slug: slug.trim() } : {}),
      });
      // createOrganization already sets the new org as active on the user row.
      await navigate({ to: "/" });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create organization"
      );
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-muted flex min-h-dvh items-center justify-center p-4">
      <form
        className="bg-background w-full max-w-md space-y-6 rounded-md border p-6 shadow-sm"
        onSubmit={onSubmit}
      >
        <div>
          <h1 className="text-xl font-semibold">Create organization</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Set up a new workspace for your business.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="org-name">Name</Label>
          <Input
            autoFocus
            disabled={submitting}
            id="org-name"
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Restaurant"
            required
            value={name}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="org-slug">Slug (optional)</Label>
          <Input
            disabled={submitting}
            id="org-slug"
            onChange={(e) => setSlug(e.target.value)}
            placeholder="acme-restaurant"
            value={slug}
          />
          <p className="text-muted-foreground text-xs">
            Auto-generated from name if left blank.
          </p>
        </div>

        {error ? (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        ) : null}

        <Button
          className="w-full"
          disabled={submitting || name.trim().length === 0}
          type="submit"
        >
          {submitting ? <Spinner className="size-4" /> : "Create organization"}
        </Button>
      </form>
    </div>
  );
}
